import { Matrix4, Vector2, Vector3 } from "three";

import ExtrudedLoopAnnotationData from "./ExtrudedLoopAnnotationData";
import SceneObjectTypeEnum from "./SceneObjectTypeEnum";
import Placement3d from "../Placement3d";
import { vector3ToJson, vector3FromJson } from "../../services/bimVisualizerApi/primitivesSerialization";
import { UUID } from "../../utils/typeDefinitions";
import Constants from "../../constants";
import { mmFromSceneUnits, sceneUnitsFromMm } from "../../utils/scaler";
import { applyOnlyRotation } from "../../utils/transformUtils";
import rectangleAnnotationDataSchema from "../../services/bimVisualizerApi/schema/document/sceneObject/rectangleAnnotationDataSchema";
import EditHandleTypeEnum from "../../enums/EditHandleTypeEnum";
import { DragMeasurement, cmValue } from "../../utils/dragMeasurement";

// A rectangle, axis-aligned to the placement's local 2D frame, defined by two opposite corners
// (offsets relative to placement.location, same world-axis convention as AreaAnnotationData.points).
// Storing the two corners as-given (rather than a derived width/height) keeps corner1 a stable
// anchor: dragging corner2 (or editing width/height) never moves corner1.
class RectangleAnnotationData extends ExtrudedLoopAnnotationData {

    corner1: Vector3;
    corner2: Vector3;

    constructor(    placement: Placement3d,
                    corner1: Vector3,
                    corner2: Vector3,
                    depth: number = 0,
                    extrudedDirection: Vector3 = new Vector3(0, 0, 1),
                    fontSize: number = RectangleAnnotationData.DEFAULT_FONT_SIZE,
                    showAreaText: boolean = true,
                    showVolume: boolean = false,
                    uuid?: UUID,
                    parentId?: UUID) {
        super(SceneObjectTypeEnum.RectangleAnnotation, placement, depth, extrudedDirection, fontSize, showAreaText, showVolume, uuid, parentId);
        this.corner1 = corner1;
        this.corner2 = corner2;
    }

    clone(): this {
        const clone = new RectangleAnnotationData(
                                        this.placement.clone() as Placement3d,
                                        this.corner1.clone(),
                                        this.corner2.clone(),
                                        this.depth,
                                        this.extrudedDirection.clone(),
                                        this.fontSize,
                                        this.showAreaText,
                                        this.showVolume,
                                        this.uuid, this.parentId) as this;

        this.copyBaseData(clone);
        return clone;
    }

    getLocalBasis(): { localX: Vector3, localY: Vector3 } {
        const localX = this.placement.refDirection.clone().normalize();
        const localY = this.placement.axis.clone().cross(localX).normalize();
        return { localX, localY };
    }

    // corner1/corner2 projected into the placement's local 2D basis, in original (not min/max) order.
    getLocalCorners(): { c1: Vector2, c2: Vector2 } {
        const { localX, localY } = this.getLocalBasis();
        return {
            c1: new Vector2(this.corner1.dot(localX), this.corner1.dot(localY)),
            c2: new Vector2(this.corner2.dot(localX), this.corner2.dot(localY)),
        };
    }

    protected getLoopPoints3d(): Vector3[] {
        const { localX, localY } = this.getLocalBasis();
        const { c1, c2 } = this.getLocalCorners();
        const minX = Math.min(c1.x, c2.x), maxX = Math.max(c1.x, c2.x);
        const minY = Math.min(c1.y, c2.y), maxY = Math.max(c1.y, c2.y);

        return ([[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY]] as const)
            .map(([x, y]) => localX.clone().multiplyScalar(x).add(localY.clone().multiplyScalar(y)));
    }

    computeEnclosedArea(): number {
        const { c1, c2 } = this.getLocalCorners();
        return Math.abs((c2.x - c1.x) * (c2.y - c1.y));
    }

    // Width × height needs no label — the "×" says what it is.
    getDragMeasurement(handleType: EditHandleTypeEnum): DragMeasurement | undefined {
        if (handleType !== EditHandleTypeEnum.RECTANGLE_CORNER) return super.getDragMeasurement(handleType);

        const { c1, c2 } = this.getLocalCorners();
        const width = Math.abs(c2.x - c1.x);
        const height = Math.abs(c2.y - c1.y);

        return { text: `${cmValue(width)} × ${cmValue(height)} cm` };
    }

    applyMatrix4(matrix: Matrix4): void {
        this.applyMatrix4Base(matrix);
        applyOnlyRotation(this.corner1, matrix);
        applyOnlyRotation(this.corner2, matrix);
    }

    toJson(): any {
        return {
            ...this.toJsonBase(),
            corner1: vector3ToJson(this.corner1, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
            corner2: vector3ToJson(this.corner2, Constants.IO_LOCATION_PRECISION, mmFromSceneUnits),
        };
    }

    static fromJson(json: any): RectangleAnnotationData {

        const parsedData = rectangleAnnotationDataSchema.parse(json);

        return new RectangleAnnotationData(
            new Placement3d(
                vector3FromJson(parsedData.placement.location, sceneUnitsFromMm),
                vector3FromJson(parsedData.placement.refDirection),
                vector3FromJson(parsedData.placement.axis)
            ),
            vector3FromJson(parsedData.corner1, sceneUnitsFromMm),
            vector3FromJson(parsedData.corner2, sceneUnitsFromMm),
            sceneUnitsFromMm(parsedData.depth),
            vector3FromJson(parsedData.extrudedDirection),
            sceneUnitsFromMm(parsedData.fontSize ?? mmFromSceneUnits(RectangleAnnotationData.DEFAULT_FONT_SIZE)),
            parsedData.showAreaText ?? true,
            parsedData.showVolume ?? false,
            parsedData.uuid,
            parsedData.parentId ?? undefined
        );
    }

}

export default RectangleAnnotationData;
